<template>
  <div class="flex-1 min-h-0 flex flex-col bg-[#FCFCFD] overflow-hidden">
    <div
      class="flex-1 min-h-0 flex flex-col overflow-y-auto w-full px-4 tablet-h:px-5 pt-[calc(env(safe-area-inset-top)+62px)] pb-[calc(env(safe-area-inset-bottom)+100px)] tablet-h:pt-10 tablet-h:pb-10"
    >
      <div class="w-full max-w-[1342px] mx-auto">
        <LinkBreadcrumb
          class="mb-5"
          :items="breadcrumbItems"
          @select="handleBreadcrumbSelect"
        />

        <!-- Header skeleton -->
        <div
          v-if="loading"
          class="w-full min-h-[120px] px-[15px] tablet-h:px-[30px] py-5 border border-[#cccccc]/50 rounded-xl flex tablet-h:items-center gap-x-5 mb-[18px]"
        >
          <Skeleton class="min-w-[80px] h-[80px] rounded-md" />
          <div class="flex-1">
            <Skeleton class="w-[200px] h-[26px] mb-3" />
            <div class="flex gap-x-10">
              <div>
                <Skeleton class="w-[120px] h-[18px] mb-1.5" />
                <Skeleton class="w-[160px] h-[18px]" />
              </div>
              <div>
                <Skeleton class="w-[100px] h-[18px] mb-1.5" />
                <Skeleton class="w-[140px] h-[18px]" />
              </div>
            </div>
          </div>
        </div>

        <!-- Header -->
        <div
          v-else
          class="w-full min-h-[120px] px-[15px] tablet-h:px-[30px] py-5 border border-[#cccccc]/50 rounded-xl flex tablet-h:items-center gap-x-5 mb-[18px]"
        >
          <div
            class="min-w-[80px] h-[80px] border border-[#cccccc]/50 rounded-md bg-[#FCFCFD] flex items-center justify-center"
          >
            <div class="size-[46px]">
              <Thumbnail
                :type="fileTypeToThumbnail(file?.type)"
                :src="previewSrc"
              />
            </div>
          </div>

          <div>
            <div class="flex mb-3 gap-x-5">
              <div class="font-medium text-[17px] leading-[26px] text-gray-800">
                {{ file?.name }}
              </div>

              <RouterLink
                :to="fileRoute"
                class="w-fit! tablet-h:hidden self-start ml-auto"
              >
                <Button variant="stroke" :size="isMobile ? 'lg' : 'md'">
                  View
                </Button>
              </RouterLink>
            </div>

            <div
              class="flex flex-col tablet-h:flex-row tablet-h:items-center gap-x-10 gap-y-4"
            >
              <div>
                <div
                  class="font-medium text-[13px] leading-[18px] text-gray-800 flex items-center mb-1.5"
                >
                  <DacrMarkIcon class="size-[16px] text-gray-800 mr-1" />
                  <div>Dacr Timestamp</div>
                </div>

                <div
                  class="text-[14px] leading-[18px] text-gray-800 flex items-center"
                >
                  {{
                    formatTimestamp(file?.copyright.registrations[0]?.timestamp)
                  }}
                </div>
              </div>

              <div>
                <div
                  class="font-medium text-[13px] leading-[18px] text-gray-800 flex items-center mb-1.5"
                >
                  <div>Dacr Token ID</div>
                </div>

                <div
                  class="text-[14px] leading-[18px] text-gray-800 flex items-center break-all"
                >
                  {{ file?.copyright.id }}
                </div>
              </div>
            </div>
          </div>

          <RouterLink
            :to="fileRoute"
            class="hidden tablet-h:block self-start ml-auto"
          >
            <Button variant="stroke" :size="isMobile ? 'lg' : 'md'">
              View registration
            </Button>
          </RouterLink>
        </div>

        <div
          class="w-full p-5 tablet-h:px-[30px] bg-white rounded-xl border-2 border-transparent border--glow relative mb-4"
        >
          <ProtectionBadge />

          <div
            class="border--glow border--glow-frame pointer-events-none absolute -inset-0.5 z-[4] rounded-xl border-2 border-transparent"
          ></div>

          <div class="flex items-center pb-5 mb-4 border-b border-gray-200">
            <FileTextIcon class="size-[18px] mr-[9px] shrink-0 text-gray-800" />
            <div
              class="w-full text-[16px] leading-[22px] font-medium text-gray-800"
            >
              Sheet music
            </div>
          </div>

          <div class="text-[14px] leading-[18px] text-gray-800">
            <div class="mb-[22px] flex items-center gap-1">
              Analyzed with
              <DacrAIIcon class="h-[13px] w-auto" aria-label="Dacr AI" />
            </div>

            <!-- Content skeleton -->
            <div v-if="sheetLoading" class="flex flex-col gap-y-4">
              <Skeleton class="w-full h-[60px]" />
              <Skeleton class="w-full h-[60px]" />
              <Skeleton class="w-full h-[60px]" />
              <Skeleton class="w-[70%] h-[60px]" />
            </div>

            <Alert v-else-if="sheetError" variant="error">
              Unable to display the sheet music for this stem.
            </Alert>

            <!-- Content -->
            <div ref="sheet"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useCopyrightFile } from '@/features/copyright/composables/useCopyrightFile'
import { formatTimestamp } from '@/shared/utils/datetime'
import { applyAuthorizationToken } from '@/shared/utils/url'
import { fileTypeToThumbnail } from '@/shared/utils/thumbnail'
import {
  Alert,
  Button,
  Skeleton,
  Thumbnail,
  LinkBreadcrumb,
  type BreadcrumbItem,
} from '@dacr/library'
import { FileTextIcon, DacrMarkIcon, DacrAIIcon } from '@dacr/icons'
import { useDevice } from '@/shared/composables/useDevice'
import { computed, ref, shallowRef, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ProtectionBadge from '@/shared/components/ProtectionBadge.vue'
import {
  OpenSheetMusicDisplay,
  FillEmptyMeasuresWithWholeRests,
} from 'opensheetmusicdisplay'
import { fetchMusicXmlDocument } from '@/features/copyright/utils/musicxml'

const route = useRoute()
const router = useRouter()

const { isMobile } = useDevice()

const sheet = ref<HTMLElement>()

const fileId = computed(() => route.params.id as string)
const trackIndex = computed(() => Number(route.params.trackIndex))
const stemName = computed(() => route.params.stem as string)

const { file, loading } = useCopyrightFile(fileId)

const previewSrc = computed(() =>
  file.value?.asset?.previewImageUrl
    ? applyAuthorizationToken(file.value.asset.previewImageUrl)
    : undefined
)

const sheetMusicUrl = computed(() => {
  const analysis = file.value?.analysis

  if (!analysis) return null

  if (
    analysis.__typename === 'AudioAnalysis' ||
    analysis.__typename === 'VideoAnalysis'
  ) {
    const track = analysis.tracks[trackIndex.value]
    const stem = track.stems.breakdown.find(
      (stem) => stem.name.toLowerCase() === stemName.value
    )

    return stem?.sheetMusic?.url
  }

  return null
})

const sheetLoading = ref(true)
const sheetError = ref(false)

const mobileSheetZoom = 0.7
const sheetDisplay = shallowRef<OpenSheetMusicDisplay>()
const sheetZoom = computed(() => (isMobile.value ? mobileSheetZoom : 1))

watch([sheetMusicUrl, sheet], async ([url, sheet]) => {
  if (!url || !sheet) {
    return
  }

  sheetError.value = false

  try {
    const musicXml = await fetchMusicXmlDocument(url)

    sheet.replaceChildren()

    const osmd = new OpenSheetMusicDisplay(sheet)
    osmd.setOptions({
      drawTitle: false,
      drawPartNames: false,
      drawCredits: false,
      drawingParameters: 'compact',
      autoGenerateMultipleRestMeasuresFromRestMeasures: false,
      fillEmptyMeasuresWithWholeRest:
        FillEmptyMeasuresWithWholeRests.YesVisible,
    })
    osmd.EngravingRules.RenderMultipleRestMeasures = false
    osmd.EngravingRules.PageLeftMargin = 0
    osmd.EngravingRules.PageRightMargin = 0
    osmd.EngravingRules.MinSkyBottomDistBetweenSystems = 2.5
    osmd.EngravingRules.MinimumDistanceBetweenSystems = 3.5
    await osmd.load(musicXml)
    osmd.Zoom = sheetZoom.value
    osmd.render()
    sheetDisplay.value = osmd
  } catch {
    sheetError.value = true
  } finally {
    sheetLoading.value = false
  }
})

watch(sheetZoom, (zoom) => {
  if (!sheetDisplay.value) return

  sheetDisplay.value.Zoom = zoom
  sheetDisplay.value.render()
})

const fileRoute = computed(() => `/file/${route.params.id}`)

const breadcrumbItems: BreadcrumbItem[] = [
  { label: 'Copyright' },
  { label: 'Sheet Music' },
]

const handleBreadcrumbSelect = (_item: BreadcrumbItem, index: number) => {
  if (index === 0) router.push(fileRoute.value)
}
</script>

<style scoped>
:global(#sheet svg) {
  width: 100% !important;
  height: 100% !important;
}
</style>
